import { ReactElement } from "react";
import { useDomainContext } from "@hooks/useDomainContext";
import MetaTags from "@components/severalbrands/metaTags";
import HeroSection from "@components/severalbrands/HeroSection";
import SecondSectionLegal from "@components/severalbrands/legalPage/secondSection";
import ThirdSectionLegal from "@components/severalbrands/legalPage/thirdSection";
import { getKeyWordsForMetaTags } from "@components/severalbrands/utils";
import { DomainInfo } from "@components/severalbrands/utils/services";

const secondSectionInfo = {
    img: "/severalbrands/assets/images/boostingClientAcquisitionImg.png",
    mainTitle: "Enhancing Client Intake for MVA Lawyers",

    paragraphs: [
        "Client acquisition is one of the toughest parts of running an MVA practice, especially for solo practitioners and small firms already juggling full caseloads. Too many lead sources deliver recycled contacts, vague ad copy, or leads that have already been shopped to multiple firms, and it can take a hundred low-quality leads to find one signable case. That combination drains both your team's time and your marketing budget.",
        "At Several Brands, we take a different approach: verified, high-intent leads generated through educational content and targeted campaigns, matched to your firm's criteria before they ever reach your intake team. The result is a more efficient intake process, so your team can spend less time chasing dead ends and more time practicing law.",
    ],

    mainSectionClassName: `3xl:gap-[200px] lg:gap-16 gap-[40px] `,
    mainTitleClassName: "text-[#351C4F]",

    imgClassName:
        "w-[285px]  h-[240px] md:w-[650px] md:min-w-[650px] md:h-[550px] relative 3xl:w-[750px] 3xl:min-w-[750px] 3xl:h-[650px] 3xl:mt-16  ",
};

const ourApproachList = [
    {
        title: "Define Your Perfect Case",
        description:
            "We start with a conversation to understand your firm's ideal case profile, geographic focus, and volume capacity, so every lead we send is a precise match for your practice.",
    },
    {
        title: "Capture Intent in Real Time",
        description:
            "Our targeted campaigns reach potential clients at the moment that matters most, right after an accident, when they're actively looking for legal help. We filter for people with legitimate claims and a clear need for representation.",
    },
    {
        title: "Verify and Deliver",
        description:
            "Before a lead reaches you, our system scrubs it for duplicates, confirms contact information, and validates incident details. Each lead is sold to one firm at a time; it is resold only if the buyer flags it as unsuitable.",
    },
    {
        title: "Performance Feedback Loop",
        description:
            "You share performance data on the leads you receive, contacted, converted, or not a fit, which helps us see what's actually working.",
    },
    {
        title: "KPI-Driven Success",
        description:
            "Together, we set clear KPIs for cost-effectiveness, and we optimize campaigns against those benchmarks to keep improving quality and conversions.",
    },
    {
        title: "Ongoing Optimization",
        description:
            "We repeat steps 2 through 4, refining continuously, until we reach a rhythm that consistently meets the KPIs we've agreed on together.",
    },
];

const growthList = [
    {
        img: "/severalbrands/assets/images/unlockBusinessSuccessB2b2.webp",
        mainTitle: "Powered by Advanced Digital Marketing Expertise",
        secondTitle: "SEO and SEM",

        paragraphs: [
            "Search engine marketing gets your firm in front of accident victims at the exact moment they're searching for legal help. Our large team of SEM professionals manages precisely targeted campaigns across Google, Bing, and Yahoo, driving high-intent traffic to conversion-focused landing pages. Alongside that, our content team builds SEO-optimized, educational content that earns organic authority over time, a slower path, but one that compounds into a durable, cost-effective source of traffic that keeps working long after a paid campaign ends.",
        ],

        mainSectionClassName: `3xl:gap-[200px] lg:gap-[130px] gap-[40px] lg:ml-16 3xl:ml-20`,
        mainTitleClassName: "text-[#351C4F]",
        secondTitleClassName: "mt-[20px] text-[#351C4F]",
        imgClassName:
            "w-[250px]  h-[250px] md:w-[420px] md:min-w-[420px] md:h-[420px] relative 3xl:w-[620px] 3xl:min-w-[620px] 3xl:h-[620px] lg:mt-10 lg:ml-16 3xl:ml-28",
    },

    {
        img: "/severalbrands/assets/images/displayAdvertisingB2b.png",
        mainTitle: "Display Advertising",
        paragraphs: [
            "Not every potential client is actively searching, so we reach them elsewhere, too. Our display strategy spans the Google Display Network, native platforms like Taboola and Outbrain, and other premium advertising networks, putting your firm in front of the right audience wherever they're already spending time online. With more than a decade of experience running these channels, we've refined our targeting to prioritize genuine conversion over simple impressions.",
        ],
        mainSectionClassName: `lg:gap-[210px] gap-[40px] lg:ml-16 3xl:ml-28 `,

        mainTitleClassName: "text-[#351C4F] 3xl:mt-[60px]",
        imgToLeft: true,
        imgClassName:
            "w-[250px]  h-[200px] md:w-[458px] md:min-w-[458px] md:h-[312px] relative 3xl:w-[630px] 3xl:min-w-[630px] 3xl:h-[554px]",
    },

    {
        img: "/severalbrands/assets/images/localizationB2b2.webp",
        mainTitle: "Localization",
        paragraphs: [
            "Reaching accident victims means doing so in the language they're most comfortable with, especially in a moment as stressful as the aftermath of a crash. For firms serving diverse communities, we build campaigns specifically localized for Spanish-speaking audiences and other language groups, adapting messaging and targeting so they resonate authentically rather than read as a translated afterthought.",
        ],
        mainSectionClassName: `3xl:gap-[210px] lg:gap-[150px]  gap-[40px]  lg:ml-16 3xl:ml-20`,
        secondSectionClassName: "",
        mainTitleClassName: "text-[#351C4F] lg:mt-10",

        imgClassName:
            "w-[250px]  h-[250px] md:w-[392px] md:min-w-[392px] md:h-[392px] 3xl:w-[592px] 3xl:min-w-[592px] 3xl:h-[592px]  relative lg:ml-16 3xl:ml-28",
    },
];

const partnerData = {
    title: "Why Partner With Us?",
    paragraphs: [
        {
            text: `At Several Brands, we're invested in your firm's growth, not just handing off leads and moving on. Every lead is sold to one firm at a time, and if something doesn't fit, a duplicate, a stale contact, or a case outside your criteria, we credit your account once it's verified, no hassle.`,
            className: "default",
        },
        {
            text: `Our pricing is performance-based and built around the metrics that actually matter to your practice: cost per lead, cost per acquisition, and ROI, not a flat retainer. Let's define what success looks like for your firm, and build a lead flow around it.`,
            className: "default",
        },
    ],
    buttonText: "Talk to Our Sales Team",
};

export default function LegalGeneralContainer(): ReactElement {
    const { domain, category } = useDomainContext();
    const description = category?.metatags?.find(
        (meta) => meta.key === "description",
    )?.value;
    return (
        <>
            <MetaTags
                title={
                    category?.title ??
                    "Auto Accident Leads for Attorneys | SeveralBrands.com"
                }
                description={
                    description ??
                    "Get high-intent, pre-screened leads sold to one firm at a time. Several Brands delivers verified MVA cases directly to your intake team. Request a quote today."
                }
                keyWords={getKeyWordsForMetaTags(
                    domain as unknown as DomainInfo,
                )}
            />
            <HeroSection />
            <SecondSectionLegal
                mainInfo={secondSectionInfo}
                approach={{ list: ourApproachList }}
            />
            <ThirdSectionLegal
                growthList={growthList}
                partnerData={partnerData}
            />
        </>
    );
}
